import { useEffect, useState } from 'react';
import { BookmarkPlus, RotateCcw, Settings, Trash2 } from 'lucide-react';
import { createPortal } from 'react-dom';

import {
  Alert,
  CloseButton,
  Field,
  Heading,
  Input,
  Pressable,
  Slider,
  Button,
  IconButton,
  NumberStepper,
} from '../../../shared/ui';

import {
  addOrUpdateTemplate,
  loadTemplates,
  MAX_TEMPLATE_NAME_LENGTH,
  MAX_WHEEL_TEMPLATES,
  removeTemplate,
  type WheelSectorTemplate,
} from './wheel-templates-storage';
import { MAX_SECTOR_NAME_LENGTH, MAX_WHEEL_SECTOR_COUNT, MIN_WHEEL_SECTOR_COUNT } from './wheel-config';
import { sectorColor } from './defaults';

export interface IWheelSettingsPanelProps {
  tempCount: number;
  tempNames: string[];
  errors: boolean[];
  userId?: number | null;
  onClose: () => void;
  onChangeCount: (n: number) => void;
  onChangeName: (index: number, name: string) => void;
  onRemoveSector: (index: number) => void;
  onReset: () => void;
  onApplyTemplate: (sectorNames: string[]) => void;
}

function sectorWord(count: number): string {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return 'сектор';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'сектора';
  return 'секторов';
}

export function WheelSettingsPanel({
  tempCount,
  tempNames,
  errors,
  userId,
  onClose,
  onChangeCount,
  onChangeName,
  onRemoveSector,
  onReset,
  onApplyTemplate,
}: IWheelSettingsPanelProps) {
  const [templates, setTemplates] = useState<WheelSectorTemplate[]>([]);
  const [templateName, setTemplateName] = useState('');
  const [saveHint, setSaveHint] = useState<string | null>(null);

  useEffect(() => {
    setTemplates(loadTemplates(userId));
  }, [userId]);

  const atLimit = templates.length >= MAX_WHEEL_TEMPLATES;
  const canUseTemplates = userId != null;

  const handleSaveTemplate = () => {
    if (!canUseTemplates) return;

    const names = tempNames.slice(0, tempCount);
    if (names.some((name) => !(name ?? '').trim())) {
      setSaveHint('Заполните все названия секторов');
      return;
    }

    const result = addOrUpdateTemplate(userId, {
      name: templateName || `Шаблон (${tempCount})`,
      sectorNames: names,
    });

    if (!result.ok) {
      if (result.reason === 'limit') {
        setSaveHint('Удалите старый шаблон — лимит 10');
      } else if (result.reason === 'quota') {
        setSaveHint('Не удалось сохранить');
      } else {
        setSaveHint('Некорректные данные шаблона');
      }
      return;
    }

    setTemplates(result.templates);
    setTemplateName('');
    setSaveHint(result.updated ? 'Шаблон обновлён' : 'Шаблон сохранён');
  };

  const handleDeleteTemplate = (id: string) => {
    if (!canUseTemplates) return;
    setTemplates(removeTemplate(userId, id));
    setSaveHint(null);
  };

  return createPortal(
    <div className="fixed inset-0 z-panel flex">
      <div className="flex-1 bg-black/25 backdrop-blur-[2px]" onClick={onClose} />
      <div className="w-full max-w-[400px] bg-white shadow-overlay flex flex-col border-l border-black/[0.08]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-black/[0.08] sticky top-0 bg-white z-10">
          <Heading size="sm" as="h2" className="flex items-center gap-2">
            <Settings size={16} className="text-primary" />
            Настройки колеса
          </Heading>
          <CloseButton className="relative z-[1]" onClick={onClose} />
        </div>

        <div className="overflow-y-auto flex-1 p-6 flex flex-col gap-6">
          <div>
            <Heading size="xs" as="h3" className="mb-1">
              Количество секторов
            </Heading>
            <p className="text-ui-xs text-ink-subtle mb-3">
              От {MIN_WHEEL_SECTOR_COUNT} до {MAX_WHEEL_SECTOR_COUNT} секторов
            </p>

            <div className="flex items-center gap-3">
              <NumberStepper
                value={tempCount}
                onChange={onChangeCount}
                min={MIN_WHEEL_SECTOR_COUNT}
                max={MAX_WHEEL_SECTOR_COUNT}
                label="Количество секторов"
                className="w-28"
              />

              <Slider
                label="Количество секторов"
                size="sm"
                min={MIN_WHEEL_SECTOR_COUNT}
                max={MAX_WHEEL_SECTOR_COUNT}
                value={tempCount}
                onChange={onChangeCount}
                className="flex-1"
              />
            </div>

            {tempCount > 12 && (
              <Alert className="mt-3" icon={<span aria-hidden>⚠️</span>}>
                При количестве &gt;12 секторов шрифт названий автоматически уменьшается
              </Alert>
            )}
          </div>

          {canUseTemplates && (
            <div>
              <Heading size="xs" as="h3" className="mb-1">
                Шаблоны
              </Heading>
              <p className="text-ui-xs text-ink-subtle mb-3">
                Сохраняются только в этом браузере на этом устройстве. Не синхронизируются и могут пропасть при очистке
                данных браузера.
              </p>

              {templates.length > 0 && (
                <div className="flex flex-col gap-2 mb-3">
                  {templates.map((template) => (
                    <div key={template.id} className="flex items-stretch gap-1.5">
                      <Pressable
                        onClick={() => {
                          onApplyTemplate(template.sectorNames);
                          setSaveHint(null);
                        }}
                        className="flex-1 min-w-0 text-left px-3 py-2 rounded-control border border-black/[0.1] bg-surface-muted hover:bg-line transition-colors"
                      >
                        <div className="text-ui-xs-strong text-ink truncate">{template.name}</div>
                        <div className="text-micro text-ink-subtle">
                          {template.sectorNames.length} {sectorWord(template.sectorNames.length)}
                        </div>
                      </Pressable>
                      <IconButton label="Удалить шаблон" size="md" onClick={() => handleDeleteTemplate(template.id)}>
                        <Trash2 size={14} />
                      </IconButton>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-2">
                <Input
                  size="sm"
                  aria-label="Название шаблона"
                  value={templateName}
                  maxLength={MAX_TEMPLATE_NAME_LENGTH}
                  onChange={(e) => {
                    setTemplateName(e.target.value);
                    setSaveHint(null);
                  }}
                  placeholder="Название шаблона"
                />
                <Button
                  type="button"
                  size="sm"
                  disabled={atLimit}
                  title={atLimit ? 'Удалите старый шаблон' : 'Сохранить текущий как шаблон'}
                  startIcon={<BookmarkPlus size={14} />}
                  onClick={handleSaveTemplate}
                >
                  Сохранить
                </Button>
              </div>

              {atLimit && (
                <p className="text-micro text-warning mt-2">
                  Лимит {MAX_WHEEL_TEMPLATES} шаблонов. Удалите старый, чтобы сохранить новый.
                </p>
              )}
              {saveHint && <p className="text-micro text-ink-subtle mt-2">{saveHint}</p>}
            </div>
          )}

          <div>
            <Heading size="xs" as="h3" className="mb-1">
              Названия секторов
            </Heading>
            <p className="text-ui-xs text-ink-subtle mb-3">Редактируйте текст — до {MAX_SECTOR_NAME_LENGTH} символов</p>

            <div className="flex flex-col gap-3">
              {Array.from({ length: tempCount }, (_, i) => (
                <div key={`sector-row-${i}`}>
                  <div className="flex items-start gap-2">
                    <div
                      className="mt-1 w-6 h-6 rounded-full shrink-0 flex items-center justify-center text-white font-medium"
                      style={{ backgroundColor: sectorColor(i), fontSize: 10 }}
                    >
                      {i + 1}
                    </div>

                    {/* A Field even without an error: the tree must not change while typing, or the input loses focus. */}
                    <Field size="sm" error={errors[i] ? 'Название не может быть пустым' : undefined} className="flex-1">
                      <Input
                        size="sm"
                        aria-label={`Название сектора ${i + 1}`}
                        value={tempNames[i] ?? ''}
                        maxLength={MAX_SECTOR_NAME_LENGTH}
                        onChange={(e) => onChangeName(i, e.target.value)}
                        placeholder={`Название сектора ${i + 1}`}
                      />
                    </Field>

                    <IconButton
                      label={
                        tempCount <= MIN_WHEEL_SECTOR_COUNT
                          ? `Минимум ${MIN_WHEEL_SECTOR_COUNT} сектора`
                          : 'Удалить сектор'
                      }
                      disabled={tempCount <= MIN_WHEEL_SECTOR_COUNT}
                      onClick={() => onRemoveSector(i)}
                    >
                      <Trash2 size={14} />
                    </IconButton>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="border-t border-black/[0.08] px-6 py-4 sticky bottom-0 bg-white">
          <Button
            type="button"
            size="md"
            variant="neutral"
            fullWidth
            startIcon={<RotateCcw size={14} />}
            onClick={onReset}
          >
            Вернуть 8 секторов
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
